'use client';

import React, { Component, type ReactNode } from 'react';
import { Text } from '~/components/ui/text';
import { Button } from '~/components/ui/button';
import { errorLogger } from '../lib/error-logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to our logging service
    errorLogger.logError(
      error,
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      },
      true // This is from an error boundary
    );

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error);
      console.error('Error Info:', errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex-1 justify-center items-center p-6">
          <Text variant="title" size="lg" className="text-center mb-4">Something went wrong</Text>
          <Text className="text-center text-gray-600 mb-6">
            We&apos;ve logged this error and will work to fix it. Please try again.
          </Text>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Text className="text-xs text-red-600 mb-4 text-center">
              {this.state.error.message}
            </Text>
          )}
          <Button onClick={this.handleReset}>Try Again</Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
